import React from "react";
import { useTranslation } from "react-i18next";
import { Target, CheckCircle2 } from "lucide-react";

/**
 * QENVA · Chemin vers 100™.
 *
 *  ┌────────────────────────────────────────────┐
 *  │ 🎯 Chemin vers 100™                         │
 *  │                                             │
 *  │  ●87        ◯93         ◯100                │
 *  │  Aujourd'hui  Après corr.  Potentiel max.   │
 *  │                                             │
 *  │  PROGRESSION VERS 100   +13 pts disponibles │
 *  │  [▓▓▓▓▓▓▓▓▓▓▓▓░░░░░░░]                       │
 *  │                                             │
 *  │  LEVIERS D'AMÉLIORATION                      │
 *  │  ✓ Correction couverture assurance  +4       │
 *  │  ✓ Correction covenant bancaire     +2       │
 *  │  ✓ Renouvellement bail commercial   +4       │
 *  │  ✓ Optimisation contrat fournisseur +3       │
 *  └────────────────────────────────────────────┘
 */
export default function PathTo100Card({ pathTo100, testId = "path-to-100" }) {
  const { t } = useTranslation("dashboard");
  const data = pathTo100 || {};
  const current = data.current_score ?? 0;
  const afterCorrections = data.after_corrections ?? current;
  const maxPotential = data.max_potential ?? 100;
  const ptsAvailable = data.points_available ?? 0;
  const levers = data.levers || [];

  const progressPct = maxPotential > 0 ? Math.round((current / maxPotential) * 100) : 0;
  const gainPct = maxPotential > 0 ? Math.round(((afterCorrections - current) / maxPotential) * 100) : 0;

  return (
    <section
      className="rounded-2xl border border-zinc-200 bg-white p-6 min-w-0 overflow-hidden"
      data-testid={testId}
    >
      <header className="flex items-center gap-2 mb-5">
        <Target size={16} className="text-qenva-blue" />
        <h2 className="font-display font-bold text-qenva-navy text-[16px] tracking-tight">
          {t("path_to_100.title")}
        </h2>
      </header>

      {/* Trail: current → after corrections → max potential */}
      <div className="flex items-start justify-between gap-2 sm:gap-4 mb-6" data-testid={`${testId}-trail`}>
        <Step value={current} label={t("path_to_100.today")} tone="info" active testId={`${testId}-step-current`} />
        <Connector />
        <Step value={afterCorrections} label={t("path_to_100.after_corrections")} tone="warning" testId={`${testId}-step-after`} />
        <Connector />
        <Step value={maxPotential} label={t("path_to_100.max_potential")} tone="success" testId={`${testId}-step-max`} />
      </div>

      {/* Progress bar */}
      <div className="mb-6" data-testid={`${testId}-progress`}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-500">{t("path_to_100.progress_label")}</span>
          <span className="text-[12px] font-bold text-emerald-600" data-testid={`${testId}-pts-available`}>
            {t("path_to_100.pts_available", { count: ptsAvailable })}
          </span>
        </div>
        <div className="relative h-2 rounded-full bg-zinc-100 overflow-hidden">
          {/* Filled blue (current) */}
          <div
            className="absolute inset-y-0 left-0 bg-qenva-blue rounded-full"
            style={{ width: `${progressPct}%` }}
          />
          {/* Future green (gain) */}
          <div
            className="absolute inset-y-0 bg-emerald-400/70 rounded-full"
            style={{ left: `${progressPct}%`, width: `${gainPct}%` }}
          />
        </div>
      </div>

      {/* Levers */}
      {levers.length > 0 && (
        <div>
          <p className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-zinc-500 mb-3">{t("path_to_100.levers_title")}</p>
          <ul className="space-y-2" data-testid={`${testId}-levers`}>
            {levers.map((lever, i) => (
              <li
                key={i}
                className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-zinc-50 border border-zinc-200/60 hover:border-qenva-blue/30 transition-colors"
                data-testid={`${testId}-lever-${i}`}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <CheckCircle2 size={14} className={leverIconClass(lever.category)} strokeWidth={2.25} />
                  <span className="text-[13px] text-qenva-navy truncate" title={lever.label}>{lever.label}</span>
                </span>
                <span className={`text-[13px] font-bold tabular-nums shrink-0 ${leverDeltaClass(lever.category)}`}>
                  +{lever.delta}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function leverIconClass(category) {
  switch (category) {
    case "recommendation": return "text-emerald-500";
    case "finding": return "text-qenva-blue";
    case "predictive": return "text-amber-500";
    case "deadline": return "text-rose-500";
    default: return "text-qenva-navy";
  }
}

function leverDeltaClass(category) {
  switch (category) {
    case "recommendation": return "text-emerald-600";
    case "finding": return "text-qenva-blue";
    case "predictive": return "text-amber-600";
    case "deadline": return "text-rose-600";
    default: return "text-qenva-navy";
  }
}

function Step({ value, label, tone, active, testId }) {
  const toneClass = {
    info: active ? "border-qenva-blue bg-qenva-blue text-white" : "border-qenva-blue/40 text-qenva-blue bg-white",
    warning: "border-amber-500 text-amber-600 bg-white",
    success: "border-emerald-500 text-emerald-600 bg-white",
  }[tone];
  return (
    <div className="flex flex-col items-center min-w-0 flex-shrink-0" data-testid={testId}>
      <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-full border-2 grid place-items-center font-display font-bold tabular-nums text-sm sm:text-base ${toneClass}`}>
        {value}
      </div>
      <span className={`text-[10px] sm:text-[11px] font-semibold mt-2 text-center leading-tight max-w-[72px] ${active ? "text-qenva-blue" : "text-zinc-500"}`}>{label}</span>
    </div>
  );
}

function Connector() {
  return <div className="flex-1 h-px bg-zinc-200 mt-5 sm:mt-7" />;
}
